// PDF.js viewer: legacy build (works on older iPhones), HTTP range loading (first page appears
// without downloading the whole file) and lazy page rendering as the user scrolls.
import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

const CDN = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}`;

function makePlaceholder(page, availableWidth, pageNum) {
  const vp = page.getViewport({ scale: 1 });
  const cssScale = availableWidth / vp.width;
  const wrap = document.createElement("div");
  wrap.style.width = `${Math.floor(vp.width * cssScale)}px`;
  wrap.style.height = `${Math.floor(vp.height * cssScale)}px`;
  wrap.style.maxWidth = "100%";
  wrap.style.margin = "0 auto 12px auto";
  wrap.style.background = "white";
  wrap.style.boxShadow = "0 2px 12px rgba(0,0,0,0.35)";
  wrap.dataset.page = String(pageNum);
  wrap.dataset.scale = String(cssScale);
  wrap.setAttribute("data-testid", `pdf-page-wrap-${pageNum}`);
  return wrap;
}

async function renderInto(pdfDoc, wrap) {
  if (wrap.dataset.rendered) return;
  wrap.dataset.rendered = "1";
  const page = await pdfDoc.getPage(Number(wrap.dataset.page));
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const viewport = page.getViewport({ scale: Number(wrap.dataset.scale) * dpr });
  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.display = "block";
  canvas.setAttribute("data-testid", `pdf-page-${wrap.dataset.page}`);
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  wrap.replaceChildren(canvas);
  page.cleanup();
}

export default function PdfJsViewer({ src, token, title }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;
    let observer = null;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const task = pdfjsLib.getDocument({
          url: src,
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : {},
          rangeChunkSize: 512 * 1024,
          disableAutoFetch: false,
          disableStream: false,
          cMapUrl: `${CDN}/cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${CDN}/standard_fonts/`,
        });
        pdfDoc = await task.promise;
        if (cancelled) return;
        const container = containerRef.current;
        if (!container) return;
        container.replaceChildren();
        const availableWidth = Math.min(container.clientWidth || 800, 1200);
        const first = await pdfDoc.getPage(1);
        const wraps = [];
        for (let n = 1; n <= pdfDoc.numPages; n++) {
          const wrap = makePlaceholder(first, availableWidth, n);
          container.appendChild(wrap);
          wraps.push(wrap);
        }
        setPages(pdfDoc.numPages);
        await renderInto(pdfDoc, wraps[0]);
        if (cancelled) return;
        setLoading(false);
        observer = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) renderInto(pdfDoc, e.target).catch((err) => console.error("PDF page error:", err));
          });
        }, { root: container.parentElement, rootMargin: "800px 0px" });
        wraps.forEach((w) => observer.observe(w));
      } catch (e) {
        console.error("PDF.js error:", e);
        if (!cancelled) { setError(e?.message || "Impossibile caricare il PDF"); setLoading(false); }
      }
    })();

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (pdfDoc) pdfDoc.destroy().catch(() => {});
    };
  }, [src, token, attempt]);

  return (
    <div className="w-full h-full overflow-y-auto overflow-x-hidden bg-neutral-900 relative" data-testid="pdf-scroll">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-20 pointer-events-none">
          <Loader2 className="animate-spin" size={36} />
          <div className="mt-3 text-sm opacity-90" data-testid="pdf-loading">Caricamento PDF...</div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-20 px-6 text-center" data-testid="pdf-error">
          <AlertCircle size={40} className="text-[#F97316]" />
          <div className="mt-3 font-semibold">Errore caricamento PDF</div>
          <div className="mt-1 text-xs opacity-80">{error}</div>
          <button onClick={() => setAttempt((a) => a + 1)} className="mt-4 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 inline-flex items-center gap-2 text-sm" data-testid="pdf-retry-btn">
            <RefreshCw size={14} /> Riprova
          </button>
        </div>
      )}
      {pages > 0 && <div className="sticky top-2 z-10 ml-auto mr-3 w-fit text-xs text-white bg-black/60 px-2 py-1 rounded-full" data-testid="pdf-page-count">{pages} pagine</div>}
      <div ref={containerRef} className="py-4 px-2" data-testid="pdf-viewer" aria-label={title || "PDF viewer"} />
    </div>
  );
}
